// src/services/matchingService.js
import api from '../api';
import { handleApiError } from '../utils/errorHandler';

export const matchingService = {
  // Provider swipes on a listing
  providerSwipe: async (listingId, direction) => {
    if (!listingId) {
      throw new Error('listingId is required');
    }
    if (!direction) {
      throw new Error('direction is required');
    }
    try {
      const response = await api.post('/matching/swipe', {
        listingId,
        direction
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to record provider swipe');
    }
  },

  // Group swipes on a provider
  groupSwipe: async (providerProfileId, direction, listingId) => {
    if (!listingId) {
      throw new Error('listingId is required for group swipes');
    }
    if (!providerProfileId) {
      throw new Error('providerProfileId is required');
    }
    if (!direction) {
      throw new Error('direction is required');
    }

    try {
      const response = await api.post('/matching/group-swipe', {
        providerProfileId,
        direction,
        listingId
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, `Failed swipe for provider ${providerProfileId}`);
    }
  },

  // Get matches for a provider
  getProviderMatches: async () => {
    try {
      const response = await api.get('/matching/provider-matches');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch provider matches');
    }
  },

  // Get matches for a specific listing
  getListingMatches: async (listingId) => {
    if (!listingId) {
      throw new Error('listingId is required');
    }
    try {
      const response = await api.get(`/matching/listing-matches/${listingId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch listing matches');
    }
  },

  // Update match status
  updateMatchStatus: async (swipeId, status) => {
    if (!swipeId) {
      throw new Error('swipeId is required');
    }
    try {
      const response = await api.patch(`/matching/match-status/${swipeId}`, { status });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to update match status');
    }
  },

  // Add notes to a match
  addMatchNotes: async (swipeId, notes) => {
    if (!swipeId) {
      throw new Error('swipeId is required');
    }
    try {
      const response = await api.patch(`/matching/match-notes/${swipeId}`, { notes });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to add match notes');
    }
  },
  
  // Mark a match as viewed by the provider
  markMatchViewed: async (swipeId) => {
    if (!swipeId) {
      throw new Error('swipeId is required');
    }
    try {
      const response = await api.patch(`/matching/mark-viewed/${swipeId}`, { viewedByProvider: true });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to mark match as viewed');
    }
  },
  
  // Browse providers for groups (optionally scoped to a listing)
  browseProviders: async (page = 1, limit = 10, excludeSwiped = true, listingId) => {
    try {
      // Build query parameters, including listing filter if provided
      const params = { page, limit, exclude_swiped: excludeSwiped };
      if (listingId) params.listingId = listingId;
      // Use the 'available' route on provider controller
      const response = await api.get('/provider/available', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to browse providers');
    }
  }
};

export default matchingService;
